import { Component, computed, inject } from '@angular/core';
import { MapWallDisplayFacade } from '@app/features/operating-picture/map-wall-display/store';
import { Dataset, DatasetSubtype, DatasetType } from '@app/modules/core/store/models';
import { CircleSvgComponent, SquareSvgComponent, TeardropSvgComponent } from './pin-shape-svgs';
import { LayerTypes } from '@app/modules/cop/store/cop';

const getUniqueSystemMarkers = ({ type, subtype }: Dataset): Function => {
  if (type === DatasetType.Assets) {
    if (subtype === DatasetSubtype.Flight) {
      return (layerColor: string) => {
        const color = layerColor.includes('#') ? layerColor.replace('#', '') : layerColor;
        return `/api/v1/meta/markers/aircraft-large/${color}/0/0/normal/aircraft-large.png`;
      };
    } else if (subtype === DatasetSubtype.Vehicle) {
      return () => `/api/v1/meta/markers/samsara-vehicle-sedan/1971c2/0/0/normal/samsara-vehicle-sedan.png`;
    }
  } else if (type === DatasetType.Camera) {
    if (subtype === DatasetSubtype.Group) {
      return (layerColor: string) => {
        const color = layerColor.includes('#') ? layerColor.replace('#', '') : layerColor;
        return `/api/v1/meta/markers/lvt-pin/${color}/0/0/normal/lvt-pin.svg`;
      };
    } else if (subtype === DatasetSubtype.Traffic) {
      return () => `/assets/images/traffic-cameras/traffic-camera-active-icon.svg`;
    }
  }
  return;
};

@Component({
    selector: 'topo-map-legend-widget',
    imports: [CircleSvgComponent, SquareSvgComponent, TeardropSvgComponent],
    templateUrl: './map-legend-widget.component.html',
    styleUrl: './map-legend-widget.component.scss'
})
export class MapLegendWidgetComponent {
  protected state = inject(MapWallDisplayFacade);

  // * NOTE * - requirement from ticket for feature work states display all non-Alert layers
  private prohibitedDatasetTypes = new Set([DatasetType.Alerts]);
  private prohibitedLayerTypes = new Set([LayerTypes.Visualization, LayerTypes.WeatherImagery, LayerTypes.HorizonLocationIntelRatings]);

  protected layersForDisplay = computed(() => {
    const { layers = [], datasetsSet = {} } = this.state.mapState() ?? {};
    const { layerVisibility = {} } = this.state.mapSettings() ?? {};

    return layers.reduce((acc, layer) => {
      const dataset = datasetsSet[layer.datasetUId];

      const isLayerVisible = !!layerVisibility[layer.uId];
      const isValidLayer = !!layer.type && !this.prohibitedLayerTypes.has(layer.type);
      const isValidDataset = !!dataset && !this.prohibitedDatasetTypes.has(dataset.type);

      if (isLayerVisible && isValidLayer && isValidDataset) {
        const uniqueSystemMarker = getUniqueSystemMarkers(dataset);

        if (Boolean(uniqueSystemMarker)) {
          acc.push({ ...layer, customMarkerImage: uniqueSystemMarker(layer.color) });
        } else {
          acc.push(layer);
        }
      }

      return acc;
    }, []);
  });
}
